import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@headlessui/react';
import { X } from 'lucide-react';

/**
 * Filtre en pastille + popover façon Stripe : pastille compacte et neutre ; quand
 * un filtre est actif, une croix à gauche l'efface directement, et le reste ouvre
 * le popover (contrôles + « Appliquer »). La valeur n'est committée qu'au clic sur
 * « Appliquer » (draft géré par le parent, réinit. à l'ouverture via onOpen).
 *
 * Composant partagé par les onglets Contrats et Frais de plateforme.
 */
export const FilterPopover: React.FC<{
  label: string;
  active: boolean;
  pillValue?: string | null;
  onOpen: () => void;
  onApply: () => void;
  onClear: () => void;
  children: React.ReactNode;
}> = ({ label, active, pillValue, onOpen, onApply, onClear, children }) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const measure = () => {
    const el = pillRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: r.left });
  };

  const pillState = active
    ? 'tw-border-gray-300 tw-text-gray-800'
    : 'tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50';

  return (
    <Popover className="tw-relative tw-inline-block">
      {({ open, close }) => (
        <>
          <div
            ref={pillRef}
            className={`tw-inline-flex tw-items-center tw-rounded-lg tw-border tw-text-[13px] tw-transition-colors ${pillState}`}
          >
            {active && (
              <button
                type="button"
                onClick={() => onClear()}
                aria-label={`Effacer le filtre ${label}`}
                className="tw-flex tw-items-center tw-pl-2 tw-pr-0.5 tw-py-1 hover:tw-opacity-70"
              >
                <X className="tw-w-3.5 tw-h-3.5" />
              </button>
            )}
            <Popover.Button
              onClick={() => {
                measure();
                onOpen();
              }}
              className={`tw-py-1 tw-outline-none ${active ? 'tw-pl-0.5 tw-pr-2.5' : 'tw-px-2.5'}`}
            >
              <span>{active && pillValue ? `${label} | ${pillValue}` : label}</span>
            </Popover.Button>
          </div>

          {open &&
            createPortal(
              <Popover.Panel
                static
                className="tw-fixed tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-gray-200 tw-w-72"
                style={{ top: `${pos.top}px`, left: `${pos.left}px`, zIndex: 9999 }}
              >
                <div className="tw-p-3 tw-space-y-3">
                  {children}
                  <button
                    type="button"
                    onClick={() => {
                      onApply();
                      close();
                    }}
                    className="tw-w-full tw-px-3 tw-py-1.5 tw-rounded-lg tw-bg-affinicar-primary tw-text-white tw-text-sm hover:tw-bg-affinicar-secondary"
                  >
                    Appliquer
                  </button>
                </div>
              </Popover.Panel>,
              document.body,
            )}
        </>
      )}
    </Popover>
  );
};
